import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../../../lib/supabaseClient';
import Spinner from '../../Atoms/Spinner';
import toast from 'react-hot-toast';
import CustomEditor from '../../../Components/TextEditor/CustomEditor';

const Container = styled.div`
    max-width: 900px;
    margin: 0 auto;
    padding: 40px 20px;
`;

const BackButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: #f5f5f5;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    color: #333;
    transition: all 0.2s ease;
    margin-bottom: 20px;

    &:hover {
        background: #e8e8e8;
        transform: translateX(-4px);
    }
`;

const Header = styled.div`
    margin-bottom: 40px;

    h1 {
        font-size: 32px;
        margin-bottom: 8px;
        color: #333;
    }

    .subtitle {
        font-size: 16px;
        color: #666;
    }

    .event-type {
        display: inline-block;
        padding: 6px 14px;
        background: linear-gradient(135deg, #f39c12, #e67e22);
        color: white;
        border-radius: 20px;
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        margin-top: 12px;
    }
`;

const FormSection = styled.div`
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
    padding: 32px;
    margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin-bottom: 24px;
    padding-bottom: 12px;
    border-bottom: 2px solid #f0f0f0;
    display: flex;
    align-items: center;
    gap: 10px;
`;

const FormGroup = styled.div`
    margin-bottom: 20px;

    label {
        display: block;
        font-size: 14px;
        font-weight: 600;
        color: #555;
        margin-bottom: 8px;
    }

    input, textarea {
        width: 100%;
        padding: 12px 16px;
        border: 1px solid #ddd;
        border-radius: 8px;
        font-size: 14px;
        transition: all 0.2s ease;
        box-sizing: border-box;

        &:focus {
            outline: none;
            border-color: #f39c12;
            box-shadow: 0 0 0 3px rgba(243, 156, 18, 0.1);
        }
    }

    textarea {
        min-height: 120px;
        resize: vertical;
    }
`;

const DualInputRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const EditorWrapper = styled.div`
    border: 1px solid #ddd;
    border-radius: 8px;
    overflow: hidden;

    .ql-toolbar {
        border: none;
        border-bottom: 1px solid #ddd;
        background: #fafafa;
    }

    .ql-container {
        border: none;
        min-height: 150px;
    }
`;

const ImagePreview = styled.div`
    margin-top: 12px;

    img {
        max-width: 300px;
        max-height: 200px;
        border-radius: 8px;
        border: 1px solid #eee;
    }

    .placeholder {
        color: #999;
        font-style: italic;
        font-size: 13px;
    }
`;

const ButtonRow = styled.div`
    display: flex;
    gap: 16px;
    margin-top: 32px;

    @media (max-width: 600px) {
        flex-direction: column;
    }
`;

const SaveButton = styled.button`
    flex: 1;
    padding: 14px 24px;
    background: linear-gradient(135deg, #f39c12, #e67e22);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(243, 156, 18, 0.4);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }
`;

const DeleteButton = styled.button`
    padding: 14px 24px;
    background: linear-gradient(135deg, #e74c3c, #c0392b);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(231, 76, 60, 0.4);
    }
`;

const PreviewLink = styled.div`
    margin-top: 24px;
    padding: 16px;
    background: #f8f9fa;
    border-radius: 8px;

    h3 {
        font-size: 14px;
        font-weight: 600;
        color: #555;
        margin-bottom: 8px;
    }

    a {
        color: #3498db;
        text-decoration: none;
        font-size: 13px;
        word-break: break-all;

        &:hover {
            text-decoration: underline;
        }
    }
`;

// Past FD100 edition event IDs
const PAST_EDITION_IDS = {
    'ec25d885-3dcf-455c-93b2-26041cb842e4': '1ère édition',
    '417b2020-721a-4c55-bd9f-d86a913f26ad': '2ème édition'
};

// Helper to get the full public URL for Supabase storage images
const getStorageUrl = (path) => {
    if (!path) return null;
    // If path is already a full URL, return as-is
    if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
    }
    // Otherwise construct the Supabase storage URL
    return `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/event-images/${path}`;
};

const FD100EditEventSupabase = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const eventId = searchParams.get('id');

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [eventData, setEventData] = useState(null);

    // Form fields
    const [titleFr, setTitleFr] = useState('');
    const [titleEn, setTitleEn] = useState('');
    const [descFr, setDescFr] = useState('');
    const [descEn, setDescEn] = useState('');
    const [location, setLocation] = useState('');
    const [eventStartDate, setEventStartDate] = useState('');
    const [eventEndDate, setEventEndDate] = useState('');
    const [inscriptionsStartDate, setInscriptionsStartDate] = useState('');
    const [inscriptionsEndDate, setInscriptionsEndDate] = useState('');

    // File uploads
    const [logoFile, setLogoFile] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    useEffect(() => {
        if (eventId) {
            fetchEventData();
        }
    }, [eventId]);

    const fetchEventData = async () => {
        try {
            setLoading(true);

            const { data, error } = await supabase
                .from('events')
                .select('*')
                .eq('id', eventId)
                .single();

            if (error) {
                console.error('Error fetching event:', error);
                toast.error('Erreur lors du chargement de l\'événement');
                return;
            }

            setEventData(data);
            setTitleFr(data.title_fr || '');
            setTitleEn(data.title_en || '');
            setDescFr(data.description_fr || '');
            setDescEn(data.description_en || '');
            setLocation(data.location || '');

            // Format dates for input fields
            if (data.event_start_date) {
                setEventStartDate(new Date(data.event_start_date).toISOString().split('T')[0]);
            }
            if (data.event_end_date) {
                setEventEndDate(new Date(data.event_end_date).toISOString().split('T')[0]);
            }
            if (data.inscriptions_start_date) {
                setInscriptionsStartDate(new Date(data.inscriptions_start_date).toISOString().split('T')[0]);
            }
            if (data.inscriptions_end_date) {
                setInscriptionsEndDate(new Date(data.inscriptions_end_date).toISOString().split('T')[0]);
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    // Handle file uploads
    const handleLogoUpload = (e) => {
        const file = e.target.files[0];
        setLogoFile(file);
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        } else {
            setLogoPreview(null);
        }
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        setImageFile(file);
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        } else {
            setImagePreview(null);
        }
    };

    const handleSave = async () => {
        // Validate dates
        if (inscriptionsStartDate && inscriptionsEndDate) {
            if (new Date(inscriptionsStartDate) > new Date(inscriptionsEndDate)) {
                toast.error('La date de début doit être avant la date de fin');
                return;
            }
        }

        setSaving(true);
        try {
            // Upload logo to Supabase Storage if a new file was selected
            let logoPath = eventData.logo_path;
            if (logoFile) {
                const logoExt = logoFile.name.split('.').pop();
                const logoFileName = `${Date.now()}-logo.${logoExt}`;
                const { data: logoData, error: logoError } = await supabase.storage
                    .from('event-images')
                    .upload(logoFileName, logoFile);

                if (logoError) {
                    console.error('Error uploading logo:', logoError);
                    toast.error('Erreur lors du téléchargement du logo');
                } else {
                    logoPath = logoData.path;
                }
            }

            // Upload main image to Supabase Storage if a new file was selected
            let imagePath = eventData.image_path;
            if (imageFile) {
                const imageExt = imageFile.name.split('.').pop();
                const imageFileName = `${Date.now()}-image.${imageExt}`;
                const { data: imageData, error: imageError } = await supabase.storage
                    .from('event-images')
                    .upload(imageFileName, imageFile);

                if (imageError) {
                    console.error('Error uploading image:', imageError);
                    toast.error('Erreur lors du téléchargement de l\'image');
                } else {
                    imagePath = imageData.path;
                }
            }

            const updateData = {
                title_fr: titleFr,
                title_en: titleEn,
                description_fr: descFr,
                description_en: descEn,
                location: location,
                event_start_date: eventStartDate || null,
                event_end_date: eventEndDate || null,
                inscriptions_start_date: inscriptionsStartDate || null,
                inscriptions_end_date: inscriptionsEndDate || null,
                logo_path: logoPath,
                image_path: imagePath
            };

            const { error } = await supabase
                .from('events')
                .update(updateData)
                .eq('id', eventId);

            if (error) {
                console.error('Error updating event:', error);
                toast.error('Erreur lors de la mise à jour');
            } else {
                toast.success('Événement mis à jour avec succès');
                navigate('/admin/events');
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Supprimer définitivement cet événement ? Cette action supprimera également tous les lauréats associés.')) {
            return;
        }

        try {
            const { error } = await supabase
                .from('events')
                .delete()
                .eq('id', eventId);

            if (error) {
                console.error('Error deleting event:', error);
                toast.error('Erreur lors de la suppression');
            } else {
                toast.success('Événement supprimé');
                navigate('/admin/events');
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Erreur lors de la suppression');
        }
    };

    if (loading) return <Spinner />;

    if (!eventData) {
        return (
            <Container>
                <BackButton onClick={() => navigate('/admin/events')}>
                    ← Retour aux événements
                </BackButton>
                <Header>
                    <h1>Événement non trouvé</h1>
                    <div className="subtitle">L'événement demandé n'existe pas dans Supabase.</div>
                </Header>
            </Container>
        );
    }

    const editionName = PAST_EDITION_IDS[eventId];

    return (
        <Container>
            <BackButton onClick={() => navigate('/admin/events')}>
                ← Retour aux événements
            </BackButton>

            <Header>
                <h1>Modifier l'événement</h1>
                <div className="subtitle">{titleFr || eventData.title_fr}</div>
                <div className="event-type">FD100 {editionName && `- ${editionName}`}</div>
            </Header>

            <FormSection>
                <SectionTitle>📝 Informations générales</SectionTitle>

                <FormGroup>
                    <label>Nom de l'événement (FR)</label>
                    <input
                        type="text"
                        value={titleFr}
                        onChange={(e) => setTitleFr(e.target.value)}
                        placeholder="Le French Design 100 - 1ère édition"
                    />
                </FormGroup>

                <FormGroup>
                    <label>Nom de l'événement (EN)</label>
                    <input
                        type="text"
                        value={titleEn}
                        onChange={(e) => setTitleEn(e.target.value)}
                        placeholder="The French Design 100 - 1st edition"
                    />
                </FormGroup>

                <FormGroup>
                    <label>Lieu</label>
                    <input
                        type="text"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        placeholder="Paris, France"
                    />
                </FormGroup>
            </FormSection>

            <FormSection>
                <SectionTitle>📅 Dates de l'événement</SectionTitle>

                <DualInputRow>
                    <FormGroup>
                        <label>Date de début de l'événement</label>
                        <input
                            type="date"
                            value={eventStartDate}
                            onChange={(e) => setEventStartDate(e.target.value)}
                        />
                    </FormGroup>

                    <FormGroup>
                        <label>Date de fin de l'événement</label>
                        <input
                            type="date"
                            value={eventEndDate}
                            onChange={(e) => setEventEndDate(e.target.value)}
                        />
                    </FormGroup>
                </DualInputRow>

                <DualInputRow style={{ marginTop: '20px' }}>
                    <FormGroup>
                        <label>Date de début des inscriptions</label>
                        <input
                            type="date"
                            value={inscriptionsStartDate}
                            onChange={(e) => setInscriptionsStartDate(e.target.value)}
                        />
                    </FormGroup>

                    <FormGroup>
                        <label>Date de fin des inscriptions</label>
                        <input
                            type="date"
                            value={inscriptionsEndDate}
                            onChange={(e) => setInscriptionsEndDate(e.target.value)}
                        />
                    </FormGroup>
                </DualInputRow>
            </FormSection>

            <FormSection>
                <SectionTitle>📄 Description (FR)</SectionTitle>
                <EditorWrapper>
                    <CustomEditor
                        value={descFr}
                        onChange={setDescFr}
                    />
                </EditorWrapper>
            </FormSection>

            <FormSection>
                <SectionTitle>📄 Description (EN)</SectionTitle>
                <EditorWrapper>
                    <CustomEditor
                        value={descEn}
                        onChange={setDescEn}
                    />
                </EditorWrapper>
            </FormSection>

            <FormSection>
                <SectionTitle>🖼️ Médias</SectionTitle>

                <DualInputRow>
                    <FormGroup>
                        <label>Logo actuel</label>
                        <ImagePreview>
                            {logoPreview ? (
                                <img src={logoPreview} alt="Nouveau logo" />
                            ) : eventData.logo_path ? (
                                <img
                                    src={getStorageUrl(eventData.logo_path)}
                                    alt="Logo"
                                    onError={(e) => e.target.style.display = 'none'}
                                />
                            ) : (
                                <span className="placeholder">Aucun logo configuré</span>
                            )}
                        </ImagePreview>
                        <label style={{ marginTop: '12px' }}>Mettre à jour le logo</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                        />
                    </FormGroup>

                    <FormGroup>
                        <label>Image de couverture actuelle</label>
                        <ImagePreview>
                            {imagePreview ? (
                                <img src={imagePreview} alt="Nouvelle image" />
                            ) : eventData.image_path ? (
                                <img
                                    src={getStorageUrl(eventData.image_path)}
                                    alt="Image"
                                    onError={(e) => e.target.style.display = 'none'}
                                />
                            ) : (
                                <span className="placeholder">Aucune image configurée</span>
                            )}
                        </ImagePreview>
                        <label style={{ marginTop: '12px' }}>Mettre à jour l'image</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                        />
                    </FormGroup>
                </DualInputRow>
            </FormSection>

            <ButtonRow>
                <SaveButton onClick={handleSave} disabled={saving}>
                    {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
                </SaveButton>
                <DeleteButton onClick={handleDelete}>
                    Supprimer l'événement
                </DeleteButton>
            </ButtonRow>

            {editionName && (
                <PreviewLink>
                    <h3>Gestion des lauréats</h3>
                    <Link to={`/admin/events/fd100-past-winners?id=${eventId}`}>
                        Voir et gérer les lauréats de la {editionName}
                    </Link>
                </PreviewLink>
            )}
        </Container>
    );
};

export default FD100EditEventSupabase;
